import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { crmApi, errMsg } from '@crm/api';
import { hasCrmPermission } from '@crm/permissions';
import { UiButton, UiInput } from '@shared/common/ui';

const DEFAULT_COLOR = '#3366cc';

export function SettingsHallsTab({ branchId }) {
  const { profile, branches } = useSelector((s) => s.crmAuth);
  const canManage = hasCrmPermission(profile, branches, branchId, 'settings.halls.manage');
  const [halls, setHalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef({});

  const loadHalls = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await crmApi(branchId).get('/halls');
      setHalls(data.halls ?? []);
    } catch (err) {
      setError(errMsg(err, 'Не удалось загрузить залы'));
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadHalls();
  }, [loadHalls]);

  useEffect(
    () => () => {
      Object.values(debounceRef.current).forEach(clearTimeout);
    },
    [],
  );

  const onAddHall = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setNotice({ type: 'error', text: 'Укажите название зала' });
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      const { data } = await crmApi(branchId).post('/halls', { name: trimmed, color });
      setHalls((list) => [...list, data.hall]);
      setName('');
      setColor(DEFAULT_COLOR);
      setNotice({ type: 'success', text: 'Зал добавлен' });
    } catch (err) {
      setNotice({ type: 'error', text: errMsg(err, 'Не удалось добавить зал') });
    } finally {
      setSaving(false);
    }
  };

  const onHallNameChange = (hallId, value) => {
    setHalls((list) => list.map((h) => (h.id === hallId ? { ...h, name: value } : h)));
    if (debounceRef.current[hallId]) clearTimeout(debounceRef.current[hallId]);
    debounceRef.current[hallId] = setTimeout(async () => {
      const trimmed = value.trim();
      if (!trimmed) return;
      try {
        const { data } = await crmApi(branchId).patch(`/halls/${hallId}`, { name: trimmed });
        setHalls((list) => list.map((h) => (h.id === hallId ? data.hall : h)));
      } catch (err) {
        setNotice({ type: 'error', text: errMsg(err, 'Не удалось сохранить название') });
        loadHalls();
      }
    }, 500);
  };

  const onDeleteHall = async (hall) => {
    const ok = window.confirm(
      'Все расписание у данного зала также будет отвязано. Вы уверены, что хотите удалить этот зал?',
    );
    if (!ok) return;
    try {
      await crmApi(branchId).delete(`/halls/${hall.id}`);
      setHalls((list) => list.filter((h) => h.id !== hall.id));
      setNotice({ type: 'success', text: 'Зал удалён' });
    } catch (err) {
      setNotice({ type: 'error', text: errMsg(err, 'Не удалось удалить зал') });
    }
  };

  return (
    <div className="settings-halls">
      {notice && (
        <div className={`events-page__notice events-page__notice--${notice.type}`}>{notice.text}</div>
      )}
      {error && <div className="form-error">{error}</div>}

      {canManage && (
        <div className="form-card form-card--wide settings-halls__add-card">
          <form className="settings-halls__add" onSubmit={onAddHall}>
            <UiInput
              label="Название зала"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Чёрный зал"
              required
            />
            <UiInput
              label="Цвет"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              required
            />
            <div className="settings-halls__add-action">
              <UiButton type="submit" disabled={saving} aria-label="Добавить зал">
                +
              </UiButton>
            </div>
          </form>
        </div>
      )}

      <div className="form-card form-card--wide settings-halls__list-card">
        {loading ? (
          <div className="muted">Загрузка…</div>
        ) : halls.length === 0 ? (
          <div className="muted">Залы не созданы.</div>
        ) : (
          <ul className="settings-halls__list">
            {halls.map((hall) => (
              <li
                key={hall.id}
                className="settings-halls__item"
                style={{ '--hall-color': hall.color || DEFAULT_COLOR }}
              >
                <UiInput
                  aria-label="Название зала"
                  value={hall.name}
                  disabled={!canManage}
                  onChange={(e) => onHallNameChange(hall.id, e.target.value)}
                />
                {canManage && (
                  <button
                    type="button"
                    className="settings-halls__delete"
                    aria-label="Удалить зал"
                    onClick={() => onDeleteHall(hall)}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
